import ProtectedPageHeader from 'components/ProtectedPageHeader'
import StatusBadge from 'components/StatusBadge'
import ScrollingTabNav from 'components/Tabs/ScrollingTabNav'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import { useHashTab } from 'components/Tabs/useHashTab'
import useFlag from 'hooks/useFlag'
import useFlagsReady from 'hooks/useFlagsReady'
import { useReportPermission } from 'hooks/useReportPermission'
import { buildTranslate } from 'locales'
import React, { useEffect } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { history } from 'store'
import { useAbility } from 'utilities/ability'
import { activationRateUrl } from 'utilities/urlUtils'
import ActivationByLocationTab from './ActivationByLocationTab'
import ActivationFunnel from './ActivationFunnel/Overview'
import ActivationRateAtSelectLocationsTab from './ActivationRateAtSelectLocationsTab'
import FirstPurchasersAndActivationsPerLocationPerMonthTab from './FirstPurchasersAndActivationsPerLocationPerMonthTab'
import LifecycleFunnelByLocationTab from './LifecycleFunnelByLocationTab'
import Main from './Main'

const t = buildTranslate('activation_rate')
export enum Tabs {
  ActivationRate = 'activation_rate',
  ActivationFunnel = 'activation_funnel',
  ActivationByLocation = 'activation_by_location',
  ActivationRateAtSelectLocations = 'activation_rate_at_select_locations',
  LifecycleFunnelByLocation = 'lifecycle_funnel_by_location',
  FirstPurchasersAndActivationsPerLocationPerMonth = 'first_purchasers_and_activations_per_location_per_month',
}

// Tabs gated on the narrower 'LocationReports' subject (see
// canAccessLocationReports below) rather than the page-level 'Reports' gate.
const LOCATION_SCOPED_TABS: Tabs[] = [
  Tabs.ActivationByLocation,
  Tabs.ActivationRateAtSelectLocations,
  Tabs.LifecycleFunnelByLocation,
  Tabs.FirstPurchasersAndActivationsPerLocationPerMonth,
]

// Picks up the four tabs this PR adds automatically. The shared resolver
// matches the hash exactly, so no ordering is needed even though
// `activation_rate` is a prefix of `activation_rate_at_select_locations`.
const ALL_TABS = Object.values(Tabs)

const ActivationRateReport: React.FC = () => {
  // Seeds from the hash and keeps following it. Matters here because the
  // sidebar's "Activation" entry links to /reports/activation#activation_rate
  // (Navbar/Customers/Content.tsx): from the Activation funnel tab that link
  // changes only the hash, so React Router re-renders rather than remounting
  // and a useState initializer would never re-run.
  const [activeTab, setActiveTab] = useHashTab(ALL_TABS, Tabs.ActivationRate)

  const handleSwitchTab = (tab: Tabs) => {
    // Spread the current location: `history.replace({ hash })` alone resets
    // `search` to '' (history 4.x createLocation), silently dropping any query
    // string when the user switches tabs.
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }
  const reportPermission = useReportPermission({
    rbac: 'Reports',
    legacy: 'LocationReports',
  })
  const ability = useAbility()
  // This page's page-level guard (above) resolves to the broad 'Reports'
  // subject under the RBAC flag, so this nested check additionally gates the
  // location-specific tab on the narrower 'LocationReports' subject —
  // mirroring the pattern used for "Signups by location" in CustomerReport.
  // Note: unlike CustomerReport, this page's legacy (flag-off) page gate is
  // already 'LocationReports', so a single check (no flag-based branching)
  // covers both regimes correctly here.
  const canAccessLocationReports = ability.can('access', 'LocationReports')
  // Gates all 4 location-scoped tabs behind the "Promote Insights Lab
  // reports" initiative flag, composed (AND) with the nested LocationReports
  // permission check above — a tab shows only when both are true.
  const isPromoteReportsEnabled = useFlag(
    'mx-promote-insights-lab-reports',
    false
  )
  const areFlagsReady = useFlagsReady()
  const canViewLocationScopedTabs =
    isPromoteReportsEnabled && canAccessLocationReports

  useEffect(() => {
    // Wait for LaunchDarkly to evaluate this merchant's context first.
    // `useFlag` is `useFlags()[key] ?? defaultValue`, so "the flag is off" and
    // "LD has not evaluated yet" both read false — and rewriting the hash on
    // that false discards a deep link to a location-scoped tab on every hard
    // refresh, before the real value arrives. The rewrite is not recoverable:
    // once the flag settles on, the hash it was pointing at is already gone.
    // FlagsReadyContext defaults to true, so trees without the provider are
    // unaffected. See hooks/useFlagsReady and CaptureRateReport, which is
    // immune only because its flag-off branch returns before touching the URL.
    if (!areFlagsReady) return

    if (
      !canViewLocationScopedTabs &&
      LOCATION_SCOPED_TABS.includes(activeTab)
    ) {
      // Mirror handleSwitchTab: keep the URL hash in sync with the tab we fall
      // back to, so a bookmarked/shared restricted hash (or a hash pointing at
      // a tab hidden behind the flag) doesn't linger in the address bar while
      // a different tab is rendered.
      //
      // Spread the location for the same reason handleSwitchTab does — a bare
      // `{ hash }` resets `search` to '' (history 4.x createLocation), so
      // falling back would silently drop the page's query string.
      history.replace({ ...history.location, hash: Tabs.ActivationRate })
      setActiveTab(Tabs.ActivationRate)
    }
  }, [areFlagsReady, canViewLocationScopedTabs, activeTab])

  // What actually renders. `activeTab` follows the hash across ALL_TABS,
  // independent of the gates, so in the window before LaunchDarkly has
  // answered it can name a location-scoped tab while the gated panes are not
  // rendered. Tab.Container would then hold an activeKey matching no TabPane
  // and no Nav.Item: a two-item tab bar with nothing highlighted and an empty
  // content area. The effect above cannot cover it — it deliberately waits
  // for readiness, which is exactly the window in question.
  //
  // Clamp for rendering only. The URL is untouched, so a permitted user's
  // deep link still resolves the moment the real flag value lands.
  const renderedTab =
    canViewLocationScopedTabs || !LOCATION_SCOPED_TABS.includes(activeTab)
      ? activeTab
      : Tabs.ActivationRate

  return (
    <>
      <Helmet>
        <title>{t(`tabs.${renderedTab}.page_title`)}</title>
      </Helmet>
      <ProtectedPageHeader
        title={t('title')}
        resource={reportPermission.resource}
        description={t('subtitle')}
        action={reportPermission.action}
        learnMoreUrl={activationRateUrl}
      >
        <Tab.Container
          activeKey={renderedTab}
          transition={false}
          onSelect={(tab: string | null) => {
            handleSwitchTab(tab as Tabs)
          }}
        >
          {/* The nav component itself is branched, not just its items: six
              tabs wrap onto a second row at common widths, which is what
              ScrollingTabNav exists for, while the two-tab row must stay
              byte-identical to master — so that branch keeps TabNav. */}
          {canViewLocationScopedTabs ? (
            <ScrollingTabNav ariaLabel={t('title')}>
              <TabItem
                state={Tabs.ActivationRate}
                title={
                  <div className="d-flex align-content-center">
                    {t('tabs.activation_rate.title')}
                    <StatusBadge type="new" />
                  </div>
                }
              />
              <TabItem
                state={Tabs.ActivationFunnel}
                title={t('tabs.activation_funnel.title')}
              />
              <TabItem
                state={Tabs.ActivationByLocation}
                title={t('tabs.activation_by_location.title')}
              />
              <TabItem
                state={Tabs.ActivationRateAtSelectLocations}
                title={t('tabs.activation_rate_at_select_locations.title')}
              />
              <TabItem
                state={Tabs.LifecycleFunnelByLocation}
                title={t('tabs.lifecycle_funnel_by_location.title')}
              />
              <TabItem
                state={Tabs.FirstPurchasersAndActivationsPerLocationPerMonth}
                title={t(
                  'tabs.first_purchasers_and_activations_per_location_per_month.title'
                )}
              />
            </ScrollingTabNav>
          ) : (
            <TabNav>
              <TabItem
                state={Tabs.ActivationRate}
                title={
                  <div className="d-flex align-content-center">
                    {t('tabs.activation_rate.title')}
                    <StatusBadge type="new" />
                  </div>
                }
              />
              <TabItem
                state={Tabs.ActivationFunnel}
                title={t('tabs.activation_funnel.title')}
              />
            </TabNav>
          )}
          <Tab.Content className="pb-l h-100 grey-05-bkg">
            <TabPane state={Tabs.ActivationRate}>
              <Main onSwitchTabAction={handleSwitchTab} />
            </TabPane>
            <TabPane state={Tabs.ActivationFunnel}>
              <ActivationFunnel onSwitchTabAction={handleSwitchTab} />
            </TabPane>
            {canViewLocationScopedTabs && (
              <TabPane state={Tabs.ActivationByLocation} mountOnEnter>
                <ActivationByLocationTab />
              </TabPane>
            )}
            {canViewLocationScopedTabs && (
              <TabPane
                state={Tabs.ActivationRateAtSelectLocations}
                mountOnEnter
              >
                <ActivationRateAtSelectLocationsTab />
              </TabPane>
            )}
            {canViewLocationScopedTabs && (
              <TabPane state={Tabs.LifecycleFunnelByLocation} mountOnEnter>
                <LifecycleFunnelByLocationTab />
              </TabPane>
            )}
            {canViewLocationScopedTabs && (
              <TabPane
                state={Tabs.FirstPurchasersAndActivationsPerLocationPerMonth}
                mountOnEnter
              >
                <FirstPurchasersAndActivationsPerLocationPerMonthTab />
              </TabPane>
            )}
          </Tab.Content>
        </Tab.Container>
      </ProtectedPageHeader>
    </>
  )
}

export default ActivationRateReport
